import { Box, SimpleGrid, Stack, Text, useMantineTheme } from '@mantine/core';
import { useState } from 'react';
import { getDemographicLabel } from '../../demographics';
import type { ThecData } from '../types';
import { CardShell } from './CardShell';

export function RepresentationCard({ data }: { data: ThecData }) {
  const theme = useMantineTheme();
  const demo = data;
  const [showAll, setShowAll] = useState(false);

  const allTimeByRace = demo.demographicsAllTime.byRace;

  const total = (Object.values(allTimeByRace) as number[]).reduce((a, b) => a + b, 0);
  const raceKeys = Array.from(
    new Set([...Object.keys(demo.tulsaPopulationPct), ...Object.keys(allTimeByRace)]),
  );
  const rows = raceKeys.map((race) => {
    const pct = demo.tulsaPopulationPct[race] ?? 0;
    const current = allTimeByRace[race] ?? 0;
    const currentPct = total > 0 ? (current / total) * 100 : 0;
    const parityLabel = currentPct >= pct ? 'At or above parity' : 'Below parity';
    return { race, pct, current, currentPct, parityLabel };
  });

  const underrepresented = rows.filter((r) => r.currentPct < r.pct);
  const visibleRows = showAll ? rows : underrepresented;

  const status = {
    label: 'Snapshot',
    color: 'gray' as const,
    tooltip: 'Parity view only, no goal tracking.',
  };

  return (
    <CardShell title="Representation Parity" subtitle={demo.participants[demo.participants.length - 1].quarter} statusLabel={status.label} statusColor={status.color}>
      <Stack spacing="sm">
        <Text size="xs" color="dimmed">
          Parity compared to Tulsa population share.
        </Text>

        <SimpleGrid
          cols={2}
          spacing="sm"
          breakpoints={[{ maxWidth: 900, cols: 1 }]}
        >
          {visibleRows.map((r) => (
              <Box
                key={r.race}
                sx={(t) => ({
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: `1px solid ${t.colorScheme === 'dark' ? t.colors.dark[4] : t.colors.gray[3]}`,
                  backgroundColor: t.colorScheme === 'dark' ? t.colors.dark[6] : t.colors.gray[0],
                })}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                    gap: 12,
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Text size="xs" color="dimmed">
                      {getDemographicLabel(r.race)}
                    </Text>
                    <Text size="sm" weight={700}>
                      Tulsa {Math.round(r.pct ?? 0)}%
                    </Text>
                    <Text size="xs" color="dimmed">
                      Current {r.currentPct.toFixed(2)}%
                    </Text>
                  </Box>
                  <Text size="xs" color="dimmed" sx={{ whiteSpace: 'nowrap' }}>
                    {r.parityLabel}
                  </Text>
                </Box>
                <Box sx={{ marginTop: 8 }}>
                  {(() => {
                    const delta = r.currentPct - r.pct;
                    const isPositive = delta >= 0;
                    // Bar shows distance from parity; cap at 50% so it stays within the track (0–50% or 50–100%)
                    const width = Math.min(50, Math.abs(delta) * 2);
                    return (
                      <Box
                        sx={{
                          position: 'relative',
                          height: 12,
                          borderRadius: 999,
                          background: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[2],
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            left: '50%',
                            top: -2,
                            width: 2,
                            height: 16,
                            transform: 'translateX(-50%)',
                            background: theme.colorScheme === 'dark' ? theme.colors.gray[4] : theme.colors.gray[6],
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 2,
                            height: 8,
                            width: `${width}%`,
                            background: isPositive ? theme.colors.blue[6] : theme.colors.yellow[6],
                            borderRadius: 999,
                            left: isPositive ? '50%' : `calc(50% - ${width}%)`,
                          }}
                        />
                      </Box>
                    );
                  })()}
                </Box>
              </Box>
          ))}
        </SimpleGrid>
        <Text
          size="xs"
          color="dimmed"
          sx={{ cursor: 'pointer', textDecoration: 'underline dotted', width: 'fit-content' }}
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll ? 'Show underrepresented only' : 'Show all groups'}
        </Text>

      </Stack>
    </CardShell>
  );
}
